// Plan 24c Phase B, Task 7 — Confirmation Dialog screen (Screen 30 — 3 variants)
//
// Full-viewport overlay shown when a destructive action needs user confirmation.
// Three variants: 'leave-game', 'kick-player', 'end-game'.
//
// SPEC EXCEPTION: The 'leave-game' variant uses a WHITE panel background (#FFFFFF).
// This is intentional per the design spec variant table — it is NOT a bug.
// All other variants use the standard dark panel: rgba(30,35,50,0.82).
// Leave-game also uses dark text colors (#1a1a2e for title) instead of white.
//
// Design tokens (docs/design/tokens.json):
//   typography.family.headline  = "'Fredoka One', 'Baloo 2', 'Arial Black', sans-serif"
//   typography.family.body      = "'Nunito Sans', 'Nunito', 'Arial Rounded MT Bold', sans-serif"
//   typography.size.h2          = "36px"
//   typography.size.body        = "16px"
//   typography.shadow.title     = "0 3px 0 #000000, 0 6px 12px rgba(0,0,0,0.50)"
//   color.text.secondary        = "#B0B8C8"
//   color.ui.overlay-dim        = "rgba(0, 0, 0, 0.60)"
//   color.ui.panel-bg-alpha     = "rgba(30, 35, 50, 0.82)"
//   spacing.lg                  = "24px"
//   spacing.md                  = "16px"
//   shadow.modal                = "0 8px 32px rgba(0,0,0,0.50)"
//   z-index.loading             = 600

import { createButton } from '@client/ui/components/button';
import type { UIComponentHandle } from '@client/ui/components/types';
import type { ScreenHandle, ScreenMountContext } from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Public types
// ─────────────────────────────────────────────────────────────────────────────

export type ConfirmDialogVariant = 'leave-game' | 'kick-player' | 'end-game';

export type ConfirmDialogPayload = {
  variant: ConfirmDialogVariant;
  /** For 'kick-player' — the target player's name to substitute into the message. */
  targetPlayerName?: string;
  /** Default for 'leave-game' only: return to warmup. Other variants require caller callbacks. */
  onConfirm?: () => void;
  /** Default = dispose the dialog without navigation. */
  onCancel?: () => void;
};

// ─────────────────────────────────────────────────────────────────────────────
// Variant content table
// ─────────────────────────────────────────────────────────────────────────────

interface VariantContent {
  title: string;
  message: (targetPlayerName?: string) => string;
  confirmLabel: string;
  cancelLabel: string;
  /** true = white panel (#FFFFFF); false = dark panel rgba(30,35,50,0.82) */
  whitePanelBg: boolean;
}

const VARIANT_CONTENT: Record<ConfirmDialogVariant, VariantContent> = {
  'leave-game': {
    title: 'Leave Game?',
    message: () => 'Your progress will be lost',
    confirmLabel: 'Leave',
    cancelLabel: 'Stay',
    whitePanelBg: true,
  },
  'kick-player': {
    title: 'Kick Player?',
    message: (name) => `Remove ${name ?? 'Player'} from the room?`,
    confirmLabel: 'Kick',
    cancelLabel: 'Cancel',
    whitePanelBg: false,
  },
  'end-game': {
    title: 'End Game?',
    message: () => 'This will end the game for all players.',
    confirmLabel: 'End Game',
    cancelLabel: 'Cancel',
    whitePanelBg: false,
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createConfirmDialogScreen(
  ctx: ScreenMountContext<ConfirmDialogPayload>,
): ScreenHandle {
  const { host, gsapRegistry, router, payload } = ctx;
  const { variant, targetPlayerName, onConfirm, onCancel } = payload;

  let disposed = false;

  const content = VARIANT_CONTENT[variant];

  // Collected button handles for disposal
  const buttonHandles: UIComponentHandle[] = [];

  // ── Action callbacks ────────────────────────────────────────────────────────

  function doConfirm(): void {
    if (onConfirm !== undefined && onConfirm !== null) {
      onConfirm();
    } else if (variant === 'leave-game') {
      router.navigate('warmup-arena-overlay', { portal: null });
    } else {
      // kick-player / end-game without callback: log and fall through to dispose
      console.warn(
        `[confirm-dialog] variant="${variant}" confirm clicked but no onConfirm provided`,
      );
    }
    dispose();
  }

  function doCancel(): void {
    if (onCancel !== undefined && onCancel !== null) {
      onCancel();
    }
    dispose();
  }

  // ── Build root overlay ──────────────────────────────────────────────────────

  const root = document.createElement('div');
  root.setAttribute('data-screen', 'confirm-dialog');
  Object.assign(root.style, {
    position: 'fixed',
    inset: '0',
    zIndex: '940',
    background: 'rgba(0,0,0,0.60)', // color.ui.overlay-dim
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
    padding:
      'max(12px, var(--app-safe-area-top)) max(12px, var(--app-safe-area-right)) max(12px, var(--app-safe-area-bottom)) max(12px, var(--app-safe-area-left))',
  });

  // ── Build centered panel ────────────────────────────────────────────────────

  const panelBg = content.whitePanelBg
    ? '#FFFFFF' // SPEC EXCEPTION: leave-game variant uses white panel
    : 'rgba(30,35,50,0.82)'; // color.ui.panel-bg-alpha

  const panel = document.createElement('div');
  Object.assign(panel.style, {
    width: '480px',
    maxWidth: 'calc(var(--app-viewport-width, 100vw) - 24px)',
    maxHeight: 'calc(var(--app-viewport-height, 100vh) - 24px)',
    overflowY: 'auto',
    overscrollBehavior: 'contain',
    boxSizing: 'border-box',
    padding: '24px 28px', // layout.panel-padding-lg
    borderRadius: '16px', // border-radius.xl
    background: panelBg,
    boxShadow: '0 8px 32px rgba(0,0,0,0.50)', // shadow.modal
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  });

  // Stop propagation so clicks on the panel do NOT bubble to the overlay (click-outside).
  panel.addEventListener('click', (e: MouseEvent) => e.stopPropagation());

  // ── Title ───────────────────────────────────────────────────────────────────

  const titleEl = document.createElement('h2');
  titleEl.textContent = content.title;
  Object.assign(titleEl.style, {
    margin: '0',
    fontFamily: "'Fredoka One', 'Baloo 2', 'Arial Black', sans-serif", // typography.family.headline
    fontSize: '36px', // typography.size.h2
    // Leave-game (white panel) = dark text; others = white
    color: content.whitePanelBg ? '#1a1a2e' : '#FFFFFF',
    textShadow: content.whitePanelBg
      ? 'none' // no dark shadow on white bg — it would look wrong
      : '0 3px 0 #000000, 0 6px 12px rgba(0,0,0,0.50)', // typography.shadow.title
    textAlign: 'center',
  });
  panel.appendChild(titleEl);

  // ── Message ──────────────────────────────────────────────────────────────────

  const messageEl = document.createElement('p');
  messageEl.textContent = content.message(targetPlayerName);
  Object.assign(messageEl.style, {
    margin: '0',
    marginTop: '24px', // spacing.lg
    fontFamily: "'Nunito Sans', 'Nunito', 'Arial Rounded MT Bold', sans-serif", // typography.family.body
    fontSize: '16px', // typography.size.body
    color: '#B0B8C8', // color.text.secondary (readable on both white and dark panels)
    textAlign: 'center',
  });
  panel.appendChild(messageEl);

  // ── Button row ───────────────────────────────────────────────────────────────

  const buttonRow = document.createElement('div');
  Object.assign(buttonRow.style, {
    display: 'flex',
    flexDirection: 'row',
    gap: '16px', // spacing.md
    justifyContent: 'center',
    marginTop: '24px', // spacing.lg
  });

  // Confirm button (Danger)
  const confirmHandle = createButton({
    gsapRegistry,
    mount: undefined,
    variant: 'danger',
    size: 'default',
    label: content.confirmLabel,
    onClick: () => {
      if (!disposed) doConfirm();
    },
  });
  buttonHandles.push(confirmHandle);
  buttonRow.appendChild(confirmHandle.element);

  // Cancel button (Primary for leave-game, Ghost for others)
  const cancelVariant = variant === 'leave-game' ? 'primary' : 'ghost';
  const cancelHandle = createButton({
    gsapRegistry,
    mount: undefined,
    variant: cancelVariant,
    size: 'default',
    label: content.cancelLabel,
    onClick: () => {
      if (!disposed) doCancel();
    },
  });
  buttonHandles.push(cancelHandle);
  buttonRow.appendChild(cancelHandle.element);

  panel.appendChild(buttonRow);

  // ── Mount root ──────────────────────────────────────────────────────────────

  root.appendChild(panel);
  host.appendChild(root);

  // ── ESC handler ─────────────────────────────────────────────────────────────

  function handleEsc(e: KeyboardEvent): void {
    if (e.key !== 'Escape') return;
    if (!disposed) doCancel();
  }

  document.addEventListener('keydown', handleEsc);

  // ── Click-outside handler ───────────────────────────────────────────────────
  // Panel stops propagation, so only clicks directly on the root overlay reach here.

  function handleClickOutside(): void {
    if (!disposed) doCancel();
  }

  root.addEventListener('click', handleClickOutside);

  // ── dispose ──────────────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    // Remove ESC listener
    document.removeEventListener('keydown', handleEsc);

    // Remove click-outside listener
    root.removeEventListener('click', handleClickOutside);

    // Dispose all button handles
    for (const handle of buttonHandles) {
      handle.dispose();
    }

    // Remove root from DOM
    root.remove();
  }

  return { element: root, dispose };
}
